import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import timezone from "dayjs/plugin/timezone";
import pluralize from "pluralize";
import Cookies from "js-cookie";
import dayjs from "dayjs";

import { NewConversationMessage } from "../../../PageView/PageViewBrieGPT/NewConversationMessage";
import { InvalidateGPTProjectQueries } from "../../../PageView/PageViewBrieGPT/useCreateProject";
import { BrieGPTTabs } from "../../../PageView/PageViewBrieGPT/BrieGPTSidebarElements/Types";
import { BrieGPTMessagePayload } from "../../../PageView/PageViewBrieGPT/PageViewBrieGPT";
import useScrollButton from "../../../PageView/PageViewBrieGPT/useScrollButton";
import useChatMessages from "../../../PageView/PageViewBrieGPT/useChatMessages";
import BrieGPTInput from "../../../PageView/PageViewBrieGPT/BrieGPTInput";
import BrieConversationAPI from "../../../../api/api/BrieConversationAPI";
import { SUPPORTED_EXTENSION_VALUES } from "../../../../utils/Constants";
import ProjectHome from "../../../PageView/PageViewBrieGPT/ProjectHome";
import { modelAtom, tenantIDAtom } from "../../../../atoms/GlobalAtoms";
import useRecord from "../../../../api/api-hooks/records/useRecord";
import { canBeIndexed, IndexFiles } from "./BrieChatWithGCHelpers";
import { FileInterface } from "../../../../types/RecordInterfaces";
import { getErrorMessage } from "../../../../utils/GeneralUtils";
import AppEmptyState from "../../../../elements/AppEmptyState";
import useUser from "../../../../api/api-hooks/auth/useUser";
import { newToastEvent } from "../../../../utils/ToastUtil";
import BrieChatWithGCSidebar from "./BrieChatWithGCSidebar";
import AppSpinner from "../../../../elements/AppSpinner";
import { AppConfirm } from "../../../../utils/PopupUtil";
import TenantAPI from "../../../../api/api/TenantAPI";
import useModule from "../../../../hooks/useModule";

const BrieComment = lazy(
  () => import("../../../PageView/PageViewBrieGPT/BrieComment")
);
dayjs.extend(timezone);

interface Props {
  recordID: string | undefined;
  moduleAPIName: string;
  recordChat: boolean;
}

const FILE_SIZE_LIMIT = 20 * 1024 * 1024; // 20 MB in bytes

export default function BrieChatWithGC({
  recordID,
  moduleAPIName,
  recordChat,
}: Props) {
  const [open, setOpen] = useState(true);
  const [model, setModel] = useAtom(modelAtom);
  const [processing, setProcessing] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const { record, recordLoading } = useRecord({ recordID, moduleAPIName });
  const { user } = useUser();
  const { record: userRecord } = useRecord({
    moduleAPIName: "Contacts",
    recordID: user?.id,
    enabled: !!user,
    fields: ["id", "_RecordName"],
  });
  const { module } = useModule(moduleAPIName);

  const messageRef = useRef<HTMLTextAreaElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const client = useQueryClient();
  const navigate = useNavigate();
  const tenantID = useAtomValue(tenantIDAtom) || "";

  const [tab, setTab] = useState<BrieGPTTabs>("Personal");
  const [searchParams, setSearchParams] = useSearchParams();
  const chatID = searchParams.get("chatID");
  const projectID = searchParams.get("projectID");
  const visible = !!(chatID || projectID) && !open && !recordLoading;
  const isNewChat = chatID === "new";

  const { messages, messagesLoading } = useChatMessages({
    type: "record",
    recordID: recordID,
    chatID: chatID === "new" ? null : chatID,
    processing: processing,
  });
  const messagesRef = useRef<BrieGPTMessagePayload[]>(messages);
  messagesRef.current = messages;

  const chatFiles = useMemo(() => {
    const fileFields = module?.Fields?.filter(
      (f) => f.FieldType === "SingleFile"
    );

    const attachments = fileFields?.flatMap((f) =>
      record?.[f.APIName] ? record?.[f.APIName] : []
    );

    if (!Array.isArray(attachments) || !attachments?.length) {
      return [];
    }

    return attachments.filter((a) => {
      if (!canBeIndexed(a)) return false;
      if (!a?.Index && !a?.hasOwnProperty("Transcript")) return false;

      return true;
    });
  }, [record, module?.Fields]);

  const fileCount = chatFiles?.length || 0;

  const { containerRef, isAtBottom, scrollToBottom } = useScrollButton(
    processing,
    [messages, responseMessage, open],
    open,
    !!messagesLoading || messages?.length === 0
  );

  // ---------------- START OF CHEEKY WORKAROUND ----------------
  // Weird drawer style bugs were occuring due to the messagesEndRef (Div at bottom of messages) when inside a Drawer.
  // This is a workaround for that. We wait for the containerRef to resize AFTER the messages have loaded (Suspensed Components)
  // and then scroll to the bottom of the containerRef. We only want to do this once!
  const isFirstChange = useRef(false);

  useEffect(() => {
    if (messagesLoading) return;
    isFirstChange.current = true;
  }, [messagesLoading]);

  useEffect(() => {
    if (!containerRef.current || !isFirstChange.current) return;

    isFirstChange.current = false;

    const resizeObserver = new ResizeObserver(() => scrollToBottom("instant"));
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  // ---------------- END OF CHEEKY WORKAROUND ----------------

  const handleSendMessage = useCallback(
    async (providedMessage?: string) => {
      let message =
        typeof providedMessage === "string" && !!providedMessage
          ? providedMessage
          : messageRef.current?.value;

      if (processing || !message || !recordID) return;

      controllerRef.current = new AbortController();

      const finalModel = model;
      const newMessages = structuredClone(messagesRef.current);

      let finalChatID = isNewChat ? null : chatID;

      const fileFields = module?.Fields?.filter(
        (f) => f.FieldType === "SingleFile"
      );

      const attachmentsData = fileFields?.flatMap((f) => {
        if (!Array.isArray(record?.[f.APIName])) return [];

        return record[f.APIName].map((file: FileInterface) => ({
          file: file,
          fieldAPIName: f.APIName,
        }));
      });

      if (!chatFiles?.length && attachmentsData?.length) {
        const filesWithSupportedType = attachmentsData?.filter((a: any) =>
          SUPPORTED_EXTENSION_VALUES.includes(a.file.Type)
        );

        if (!filesWithSupportedType?.length) {
          return AppConfirm({
            title: "Invalid File Types",
            text: "You have not added any supported files to this Library. Add files of supported types to continue.",
            confirmButtonText: "OK",
          });
        }

        const filesWithCorrectSize = attachmentsData?.filter(
          (a: any) => a.file.Size < FILE_SIZE_LIMIT
        );

        if (!filesWithCorrectSize?.length) {
          return AppConfirm({
            title: "File Size Exceeded",
            text: "All added files that exceed the maximum file size limit. Add a file smaller than 20mb to continue.",
            confirmButtonText: "OK",
          });
        }

        const indexedFiles = attachmentsData?.filter(
          (a: any) => a.file?.Index || a.file?.hasOwnProperty("Transcript")
        );

        if (!indexedFiles?.length) {
          const result = await AppConfirm({
            title: "Missing Index",
            text: "All added files have not been indexed. Would you like to index them now?",
            confirmButtonText: "Yes",
            confirmButtonColor: "var(--primary)",
          });

          if (!result.isConfirmed) return;

          newToastEvent(
            "info",
            "Indexing files... Please wait a few minutes.",
            "indexing",
            { autoClose: false, loading: true }
          );

          const filesToIndex = attachmentsData?.filter(
            (a) =>
              (!a.file.Index || !a.file.hasOwnProperty("Transcript")) &&
              a.file.Size < FILE_SIZE_LIMIT &&
              SUPPORTED_EXTENSION_VALUES.includes(a.file.Type)
          );

          setIndexing(true);

          await IndexFiles(
            filesToIndex,
            recordID,
            moduleAPIName,
            navigate,
            tenantID!,
            client
          );

          return setIndexing(false);
        }
      }

      setProcessing(true);

      newMessages.push({
        Content: message,
        Role: "user",
        ChatID: finalChatID || "",
        Record: { id: recordID, _ModuleAPIName: moduleAPIName },
        ModelName: model,
        CreatedBy: userRecord,
      });

      client.setQueryData(
        [tenantID, recordID, finalChatID, "BrieGPTMessages"],
        newMessages
      );

      if (!providedMessage) {
        messageRef.current!.value = "";
      }

      if (isNewChat || !chatID) {
        let createData;

        try {
          createData = await BrieConversationAPI.createBrieGPTChat({
            type: "record",
            recordID: recordID,
            moduleAPIName: moduleAPIName,
            projectID: projectID || undefined,
            message: message,
          }).then((res) => res.data.data);
        } catch (err) {
          newToastEvent("error", getErrorMessage(err));
          setProcessing(false);
        }

        finalChatID = createData?.chatID;
        if (projectID) {
          InvalidateGPTProjectQueries({
            tab: tab,
            tenantID,
            itemID: record?.id,
            client,
          });
        } else {
          client.invalidateQueries({
            queryKey: [tenantID, record?.id, "BrieGPTChats"],
          });
        }
        client.setQueryData(
          [tenantID, recordID, finalChatID, "BrieGPTMessages"],
          newMessages
        );
        client.removeQueries({
          queryKey: [tenantID, recordID, null, "BrieGPTMessages"],
        });

        searchParams.set("chatID", finalChatID!);
        setSearchParams(searchParams);
      }

      const finalToken =
        Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`) ||
        "undefined";

      const response = await fetch(
        `${window.GC_API_URL}/tenant/brie-conversations/brieGPT/chat`,
        {
          method: "POST",
          headers: {
            "app-id": "GrowthCloud",
            "Content-Type": "application/json",
            Authorization: `Bearer ${finalToken}`,
          },
          body: JSON.stringify({
            type: "record",
            attachments: chatFiles,
            message: message,
            model: model,
            chatID: finalChatID,
            recordID: recordID,
            moduleAPIName: moduleAPIName,
            useRecord: recordChat,
            projectID: projectID,
            timeZone: dayjs.tz.guess(),
          }),
          signal: controllerRef.current?.signal,
          credentials: "include",
        }
      );

      if (!response.ok || !response.body) {
        client.setQueryData(
          [tenantID, recordID, finalChatID, "BrieGPTMessages"],
          (prev: any) => [
            ...(prev || []),
            {
              Content: "Something went wrong. Please try again.",
              Role: "assistant",
              ChatID: chatID || "",
              Record: { id: recordID, _ModuleAPIName: moduleAPIName },
              ModelName: finalModel,
            },
          ]
        );
        return setProcessing(false);
      }

      // If the server is returning in streaming mode, response.body is a ReadableStream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let responseText = "";

      while (true) {
        let value;
        let done = false;
        let chunk = "";

        try {
          const result = await reader.read();
          value = result.value;
          done = result.done;
          chunk = decoder.decode(value);
        } catch (err) {
          if (controllerRef.current?.signal.aborted) {
            setProcessing(false);
            responseText += "\n\n[Stopped Processing]";
            client.setQueryData(
              [tenantID, recordID, finalChatID, "BrieGPTMessages"],
              (prev: any) => [
                ...(prev || []),
                {
                  Content: responseText,
                  Role: "assistant",
                  ChatID: finalChatID || "",
                  Record: { id: recordID, _ModuleAPIName: moduleAPIName },
                  ModelName: finalModel,
                },
              ]
            );
            setResponseMessage("");
            return;
          }

          client.setQueryData(
            [tenantID, recordID, finalChatID, "BrieGPTMessages"],
            (prev: any) => [
              ...(prev || []),
              {
                Content: "Something went wrong. Please try again.",
                Role: "assistant",
                ChatID: finalChatID || "",
                Record: { id: recordID, _ModuleAPIName: moduleAPIName },
                ModelName: finalModel,
              },
            ]
          );
          setResponseMessage("");
          return setProcessing(false);
        }

        if (done || chunk.endsWith("[DONE]")) {
          if (chunk.endsWith("[DONE]")) {
            chunk = chunk.replace("[DONE]", "");
            responseText += chunk;
          }

          setResponseMessage("");
          client.setQueryData(
            [tenantID, recordID, finalChatID, "BrieGPTMessages"],
            (prev: any) => [
              ...(prev || []),
              {
                Content: responseText,
                Role: "assistant",
                ChatID: finalChatID || "",
                Record: { id: recordID, _ModuleAPIName: moduleAPIName },
                ModelName: finalModel,
              },
            ]
          );
          break;
        }

        responseText += chunk;
        setResponseMessage(responseText);
      }

      setProcessing(false);
    },
    [
      processing,
      messageRef,
      recordID,
      model,
      chatID,
      module?.Fields,
      record,
      client,
      tenantID,
      isNewChat,
      moduleAPIName,
      searchParams,
      chatFiles,
    ]
  );

  const onStop = useCallback(() => {
    controllerRef.current?.abort();
    setProcessing(false);
  }, []);

  /** Added this to not show the sidebar when clicking on a project. */
  useEffect(() => {
    if (projectID) {
      setOpen(false);
    }
  }, [projectID]);

  if (!chatID && !open && !projectID) {
    return (
      <>
        <BrieChatWithGCSidebar
          open={open}
          setOpen={setOpen}
          recordID={recordID}
          moduleAPIName={moduleAPIName}
          tab={tab}
          setTab={setTab}
        />
        <AppEmptyState
          className="tw:mt-4 tw:mx-4"
          icon="mdi-comment-text-outline"
          title="No Chat Selected"
          description="Select or create a chat to start a conversation with your files."
          buttonTitle="Open Chat Menu"
          onClick={() => setOpen(true)}
        />
      </>
    );
  }

  return (
    <>
      <BrieChatWithGCSidebar
        open={open}
        setOpen={setOpen}
        recordID={recordID}
        moduleAPIName={moduleAPIName}
        tab={tab}
        setTab={setTab}
      />
      {visible && messagesLoading && chatID && (
        <div className="tw:w-full tw:flex tw:justify-center tw:items-center">
          <AppSpinner size="lg" />
        </div>
      )}
      <div
        className="tw:flex tw:flex-col tw:h-full tw:overflow-y-auto"
        ref={containerRef}
      >
        {visible && (
          <>
            <div className="tw:px-2 tw:pb-16 tw:grow">
              {processing ? null : projectID && !chatID ? (
                <div className="tw:m-3">
                  <ProjectHome
                    tab={tab}
                    setTab={setTab}
                    type="record"
                    recordID={recordID || ""}
                    moduleAPIName={moduleAPIName}
                  />
                </div>
              ) : isNewChat ? (
                <NewConversationMessage />
              ) : null}
              {messages.map((message, i) => {
                const isBrie = message.Role === "assistant";

                if (!isBrie && message.Role !== "user") return null;

                if (isBrie) {
                  return (
                    <Suspense fallback={null} key={i}>
                      <BrieComment
                        comment={message}
                        css={"tw:justify-start tw:max-w-full tw:w-full"}
                        innerCSS={"tw:w-full tw:px-3"}
                        backgroundColor={"transparent"}
                        copyButtonCSS={"tw:-mt-5 tw:justify-start"}
                      />
                    </Suspense>
                  );
                }

                return (
                  <Suspense fallback={null}>
                    <BrieComment
                      comment={message}
                      key={i}
                      css={"tw:justify-end tw:mb-4"}
                      backgroundColor={"#F0F3F2"}
                      maxWidth={650}
                      innerCSS={"tw:px-3 btn-oauth"}
                      useMarkdown={false}
                    />
                  </Suspense>
                );
              })}
              {processing && !responseMessage && (
                <div className="tw:text-sm flow-text tw:font-medium tw:max-w-full tw:pl-4 tw:pt-4">
                  <span className="flow-text-inner">
                    BrieGPT is processing...
                  </span>
                </div>
              )}
              {responseMessage && (
                <Suspense fallback={null}>
                  <BrieComment
                    comment={{
                      Content: responseMessage,
                      Role: "assistant",
                      ChatID: chatID || "",
                      Record: {
                        id: recordID || "",
                        _ModuleAPIName: moduleAPIName,
                      },
                      ModelName: model,
                      CreatedBy: userRecord,
                    }}
                    css="tw:justify-start tw:max-w-full tw:w-full"
                    innerCSS={"tw:w-full tw:px-3"}
                    backgroundColor="transparent"
                  />
                </Suspense>
              )}
            </div>
            {!isAtBottom && (
              <div
                className="tw:sticky tw:bottom-46 tw:rounded-full tw:border tw:border-gray-300 btn-oauth tw:w-8 tw:h-8 tw:flex tw:justify-center tw:cursor-pointer tw:hover:bg-superlight tw:hover:text-dark tw:bg-white tw:mx-auto"
                onClick={() => {
                  scrollToBottom();
                }}
              >
                <i className="mdi mdi-chevron-down tw:leading-8 tw:text-2xl" />
              </div>
            )}
            <div className="tw:sticky tw:bottom-0 tw:w-full tw:z-1 tw:px-4 tw:flex-none">
              <BrieGPTInput
                files={undefined}
                setFiles={undefined}
                processing={processing || indexing}
                messageRef={messageRef}
                model={model}
                setModel={setModel}
                handleSendMessage={handleSendMessage}
                onStop={onStop}
                message={
                  indexing
                    ? `Indexing Files...`
                    : !!fileCount
                    ? `Chatting with ${
                        recordChat ? "Record and " : ""
                      }${fileCount} ${pluralize("file", fileCount)}`
                    : recordChat
                    ? "Chatting with Record"
                    : ""
                }
                chatID={chatID}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
